import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Wallet, ArrowUpRight, ArrowDownRight, Clock, CheckCircle2, BanknoteIcon, Building2 } from 'lucide-react';

export default function FreelancerDashboard() {
  const { api, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showBankForm, setShowBankForm] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankForm, setBankForm] = useState({ account_name: '', account_number: '', ifsc_code: '', bank_name: '', upi_id: '' });

  useEffect(() => {
    Promise.all([
      api.get('/tasks'),
      api.get('/freelancer/earnings'),
      api.get('/freelancer/bank-details'),
      api.get('/freelancer/withdrawals'),
    ]).then(([t, e, b, w]) => {
      setTasks(t.data);
      setEarnings(e.data);
      setBankDetails(b.data);
      setWithdrawals(w.data);
    }).catch(console.error);
  }, [api]);

  const saveBankDetails = async () => {
    await api.post('/freelancer/bank-details', bankForm);
    setBankDetails(bankForm);
    setShowBankForm(false);
  };

  const requestWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) return;
    await api.post('/freelancer/withdraw', { amount: amt, mode: 'NEFT' });
    setShowWithdraw(false);
    setWithdrawAmount('');
    const [e, w] = await Promise.all([api.get('/freelancer/earnings'), api.get('/freelancer/withdrawals')]);
    setEarnings(e.data);
    setWithdrawals(w.data);
  };

  const inProgress = tasks.filter(t => t.status === 'in_progress');
  const pending = tasks.filter(t => t.status === 'not_started');
  const completed = tasks.filter(t => t.status === 'completed');

  return (
    <div className="space-y-6" data-testid="freelancer-dashboard">
      <div>
        <h1 className="text-2xl font-heading font-bold text-white">My Dashboard</h1>
        <p className="text-xs text-zinc-500 mt-1">Welcome back, {user?.full_name}</p>
      </div>

      {/* Earnings Cards */}
      {earnings && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2"><Wallet className="w-4 h-4 text-emerald-400" /><span className="text-[10px] text-zinc-500 uppercase">Available</span></div>
            <p className="text-xl font-heading font-bold text-emerald-400 font-mono">{earnings.available_balance?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-amber-400" /><span className="text-[10px] text-zinc-500 uppercase">Pending</span></div>
            <p className="text-xl font-heading font-bold text-amber-400 font-mono">{earnings.pending_amount?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2"><ArrowUpRight className="w-4 h-4 text-blue-400" /><span className="text-[10px] text-zinc-500 uppercase">Total Earned</span></div>
            <p className="text-xl font-heading font-bold text-blue-400 font-mono">{earnings.total_earned?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2"><ArrowDownRight className="w-4 h-4 text-zinc-400" /><span className="text-[10px] text-zinc-500 uppercase">Withdrawn</span></div>
            <p className="text-xl font-heading font-bold text-zinc-300 font-mono">{earnings.total_withdrawn?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
          </div>
        </div>
      )}

      {/* Actions Row */}
      <div className="flex gap-3">
        <button data-testid="withdraw-button" onClick={() => setShowWithdraw(true)} className="h-8 px-4 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-sm transition-all">
          Transfer to Bank
        </button>
        <button data-testid="bank-details-button" onClick={() => setShowBankForm(true)} className="h-8 px-4 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium rounded-sm transition-all border border-zinc-700">
          {bankDetails && bankDetails.account_number ? 'Update Bank Details' : 'Add Bank Details'}
        </button>
      </div>

      {/* Withdraw Modal */}
      {showWithdraw && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="withdraw-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-96">
            <h3 className="text-sm font-heading font-semibold text-white mb-4">Transfer to Bank</h3>
            <p className="text-xs text-zinc-500 mb-3">Available: <span className="text-emerald-400 font-mono">{earnings?.available_balance?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span></p>
            <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="Enter amount" className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white mb-3 focus:ring-1 focus:ring-blue-500 focus:outline-none" />
            <div className="flex gap-2">
              <button onClick={requestWithdraw} className="flex-1 h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded-sm">Confirm</button>
              <button onClick={() => setShowWithdraw(false)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">MOCK: RazorpayX payout - sandbox mode</p>
          </div>
        </div>
      )}

      {/* Bank Form Modal */}
      {showBankForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="bank-form-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-96">
            <h3 className="text-sm font-heading font-semibold text-white mb-4">Bank Details</h3>
            {['account_name', 'account_number', 'ifsc_code', 'bank_name', 'upi_id'].map(field => (
              <div key={field} className="mb-2">
                <label className="text-[10px] text-zinc-500 uppercase">{field.replace(/_/g, ' ')}</label>
                <input value={bankForm[field]} onChange={e => setBankForm({ ...bankForm, [field]: e.target.value })} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" />
              </div>
            ))}
            <div className="flex gap-2 mt-3">
              <button onClick={saveBankDetails} className="flex-1 h-8 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm">Save</button>
              <button onClick={() => setShowBankForm(false)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* My Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* In Progress */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">In Progress ({inProgress.length})</h3>
          </div>
          <div className="divide-y divide-zinc-800/30 max-h-80 overflow-y-auto">
            {inProgress.map(t => (
              <div key={t.id} className="px-4 py-3">
                <p className="text-xs text-zinc-200">{t.title || t.name || t.parent_code || (t.task_type ? t.task_type.replace(/_/g, ' ').toUpperCase() : 'Task')}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-mono text-zinc-500">{t.task_type}</span>
                  <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${t.progress}%` }} /></div>
                  <span className="text-[10px] font-mono text-zinc-500">{t.progress}%</span>
                </div>
                <p className="text-[10px] font-mono text-emerald-400 mt-1">{t.rate_amount?.toLocaleString()}</p>
              </div>
            ))}
            {inProgress.length === 0 && <p className="text-xs text-zinc-600 px-4 py-6 text-center">No tasks in progress</p>}
          </div>
        </div>

        {/* Pending */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-zinc-500" />
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Pending ({pending.length})</h3>
          </div>
          <div className="divide-y divide-zinc-800/30 max-h-80 overflow-y-auto">
            {pending.map(t => (
              <div key={t.id} className="px-4 py-3">
                <p className="text-xs text-zinc-300">{t.title || t.name || t.parent_code || (t.task_type ? t.task_type.replace(/_/g, ' ').toUpperCase() : 'Task')}</p>
                <span className="text-[10px] font-mono text-zinc-600">{t.task_type} | {t.rate_amount?.toLocaleString()}</span>
              </div>
            ))}
            {pending.length === 0 && <p className="text-xs text-zinc-600 px-4 py-6 text-center">No pending tasks</p>}
          </div>
        </div>

        {/* Completed */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Completed ({completed.length})</h3>
          </div>
          <div className="divide-y divide-zinc-800/30 max-h-80 overflow-y-auto">
            {completed.map(t => (
              <div key={t.id} className="px-4 py-3">
                <p className="text-xs text-zinc-300">{t.title || t.name || t.parent_code || (t.task_type ? t.task_type.replace(/_/g, ' ').toUpperCase() : 'Task')}</p>
                <span className="text-[10px] font-mono text-emerald-400">{t.rate_amount?.toLocaleString()} earned</span>
              </div>
            ))}
            {completed.length === 0 && <p className="text-xs text-zinc-600 px-4 py-6 text-center">No completed tasks</p>}
          </div>
        </div>
      </div>

      {/* Withdrawal History */}
      {withdrawals.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-800">
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Withdrawal History</h3>
          </div>
          <div className="divide-y divide-zinc-800/30">
            {withdrawals.map(w => (
              <div key={w.id} className="px-4 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono text-zinc-300">{w.amount?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                  <p className="text-[10px] text-zinc-600">{w.mode} | {w.payout_id}</p>
                </div>
                <span className={`status-${w.status === 'completed' ? 'completed' : 'pending'} px-1.5 py-0.5 rounded-full text-[9px] font-medium`}>{w.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
